import { Injectable, computed, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { creditsToDollarLabel } from 'src/app/utils/credits';

/** A member's bypass of their role budget (set by an admin in the admin console). */
export interface MemberBudgetBypass {
  mode: 'unlimited' | 'custom';
  monthlyCredits?: number;
  /** Epoch ms; null = no expiry. */
  expiresAtMs: number | null;
}

/**
 * The signed-in member's monthly budget in the active org — `getMyBudget`
 * (billing codebase). Credits: 100 = $1, fees included.
 */
export interface MemberBudget {
  /** UTC calendar month, `YYYY-MM`. */
  period: string;
  /** null = no limit for this member (role without a budget, or an unlimited bypass). */
  limitCredits: number | null;
  spentCredits: number;
  bypass: MemberBudgetBypass | null;
  hardStop: boolean;
  notifyAtPercent: number;
  /** Over the limit with hard stop on: their agents and chats are paused. */
  blocked: boolean;
}

/** What the user menu shows: a translation key and its params, or nothing. */
export interface MemberBudgetMeter {
  key: string;
  params: Record<string, string>;
}

const REFRESH_MS = 5 * 60_000;

/** Timestamps arrive serialized through the callable: ms, ISO string or {seconds|_seconds}. */
function toMillis(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'object') {
    const v = value as { seconds?: number; _seconds?: number; toMillis?: () => number };
    if (typeof v.toMillis === 'function') return v.toMillis();
    const seconds = v.seconds ?? v._seconds;
    return typeof seconds === 'number' ? seconds * 1000 : null;
  }
  return null;
}

function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Callable payload → MemberBudget; null when it is not a usable answer. Pure — unit tested. */
export function toMemberBudget(raw: unknown): MemberBudget | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.period !== 'string' || !r.period) return null;
  const limit = r.limitCredits === null || r.limitCredits === undefined ? null : num(r.limitCredits, NaN);
  const rawBypass = r.bypass as Record<string, unknown> | null | undefined;
  const bypass: MemberBudgetBypass | null = rawBypass && (rawBypass.mode === 'unlimited' || rawBypass.mode === 'custom')
    ? {
      mode: rawBypass.mode,
      ...(rawBypass.monthlyCredits !== undefined ? { monthlyCredits: num(rawBypass.monthlyCredits) } : {}),
      expiresAtMs: toMillis(rawBypass.expiresAt),
    }
    : null;
  return {
    period: r.period,
    limitCredits: limit === null || Number.isNaN(limit) ? null : limit,
    spentCredits: Math.max(0, num(r.spentCredits)),
    bypass,
    hardStop: r.hardStop !== false,
    notifyAtPercent: num(r.notifyAtPercent, 80) || 80,
    blocked: r.blocked === true,
  };
}

/** The user-menu line for a budget, or null when there is nothing to show. Pure — unit tested. */
export function budgetMeter(budget: MemberBudget | null, formatDate: (ms: number) => string): MemberBudgetMeter | null {
  if (!budget) return null;
  if (budget.bypass?.mode === 'unlimited') {
    return budget.bypass.expiresAtMs
      ? { key: 'budget.meter-unlimited-until', params: { date: formatDate(budget.bypass.expiresAtMs) } }
      : { key: 'budget.meter-unlimited', params: {} };
  }
  if (budget.limitCredits === null) return null;
  return {
    key: 'budget.meter',
    params: { spent: creditsToDollarLabel(budget.spentCredits), limit: creditsToDollarLabel(budget.limitCredits) },
  };
}

/** Spent at or past the notify threshold of a real limit (not blocked yet). Pure — unit tested. */
export function budgetWarning(budget: MemberBudget | null): boolean {
  if (!budget || budget.blocked || budget.limitCredits === null || budget.limitCredits <= 0) return false;
  if (budget.bypass?.mode === 'unlimited') return false;
  return budget.spentCredits >= budget.limitCredits * (budget.notifyAtPercent / 100);
}

/**
 * The member's monthly budget (admin console → Budgets): fetched on org
 * change, every 5 minutes and when the window regains focus. Fails soft —
 * without the callable (older backend, other region) nothing is shown.
 */
@Injectable({ providedIn: 'root' })
export class MemberBudgetService {
  private readonly firebase = inject(FirebaseService);
  private readonly organization = inject(OrganizationService);

  readonly budget = signal<MemberBudget | null>(null);
  readonly meter = computed(() => budgetMeter(this.budget(), (ms) => new Date(ms).toLocaleDateString()));
  readonly warning = computed(() => budgetWarning(this.budget()) && !this.dismissedFor(this.budget()));
  readonly blocked = computed(() => this.budget()?.blocked === true);
  readonly requested = signal(false);
  /** Bumped when a dismissal is stored so `warning` re-reads it. */
  private readonly dismissTick = signal(0);

  private started = false;
  private orgSub?: Subscription;
  private timer?: ReturnType<typeof setInterval>;
  private readonly onFocus = () => void this.refresh();

  start(): void {
    if (this.started) return;
    this.started = true;
    this.orgSub = this.organization.orgId$.subscribe(() => void this.refresh());
    this.timer = setInterval(() => void this.refresh(), REFRESH_MS);
    if (typeof window !== 'undefined') window.addEventListener('focus', this.onFocus);
  }

  stop(): void {
    this.started = false;
    this.orgSub?.unsubscribe();
    if (this.timer) clearInterval(this.timer);
    if (typeof window !== 'undefined') window.removeEventListener('focus', this.onFocus);
  }

  private get orgId(): string | null {
    return this.organization.data?.id ?? this.organization.orgId$.getValue() ?? null;
  }

  async refresh(): Promise<void> {
    const organizationId = this.orgId;
    if (!organizationId) {
      this.budget.set(null);
      return;
    }
    try {
      const result = await this.firebase.functions.call('getMyBudget', { organizationId });
      const budget = toMemberBudget((result as { data?: unknown })?.data ?? result);
      this.budget.set(budget);
      this.requested.set(!!budget && this.readFlag(this.requestKey(organizationId, budget.period)));
    } catch {
      this.budget.set(null);
    }
  }

  /** Ask the org admins for more budget this month (requestBudgetIncrease). */
  async requestIncrease(message?: string): Promise<void> {
    const organizationId = this.orgId;
    const budget = this.budget();
    if (!organizationId || !budget) return;
    const text = (message ?? '').trim();
    await this.firebase.functions.call('requestBudgetIncrease', { organizationId, ...(text ? { message: text.slice(0, 500) } : {}) });
    this.writeFlag(this.requestKey(organizationId, budget.period));
    this.requested.set(true);
  }

  /** Hide the 80% warning for the rest of this budget period. */
  dismissWarning(): void {
    const organizationId = this.orgId;
    const budget = this.budget();
    if (!organizationId || !budget) return;
    this.writeFlag(this.dismissKey(organizationId, budget.period));
    this.dismissTick.update((n) => n + 1);
  }

  private dismissedFor(budget: MemberBudget | null): boolean {
    this.dismissTick();
    const organizationId = this.orgId;
    return !!budget && !!organizationId && this.readFlag(this.dismissKey(organizationId, budget.period));
  }

  private dismissKey(orgId: string, period: string): string {
    return `crewdle_budget_warning_dismissed_${orgId}_${period}`;
  }

  private requestKey(orgId: string, period: string): string {
    return `crewdle_budget_requested_${orgId}_${period}`;
  }

  private readFlag(key: string): boolean {
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
  }

  private writeFlag(key: string): void {
    try { localStorage.setItem(key, '1'); } catch { /* private mode: the flag lasts this session only */ }
  }
}
